import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { SourceDocument } from "../../../../api/content/sourcesApi";
import { appPaths } from "../../../../shared/routing/appPaths";
import EmptyState from "../../../../ui/feedback/EmptyState";
import { classNames } from "../../../../ui/styles/classNames";
import { getAnnotationLabelOption, type AnnotationLabelOption } from "../../../annotation/annotationLabels";
import type { SpannedAnnotationEntity } from "../../../annotation/annotationSpans";
import AnnotationTextEditor, { type EditableAnnotationEntity } from "../../../annotation/AnnotationTextEditor";
import type { EvidenceLayer, EvidenceMark } from "../../../annotation/textEvidence";
import type { ChatReference } from "../../../chatReferences";
import { placeAnnotations, placeEvidence, type IndexedAnnotation } from "../../../annotation/annotationPlacement";
import UnplacedAnnotations from "../../../annotation/UnplacedAnnotations";
import { segmentDisplayName } from "../documentNames";
import { pageMapOf, SOURCE_URL_METADATA_KEY } from "../pageMap";
import SegmentPages from "../SegmentPages";
import AskAboutSegment from "./AskAboutSegment";

/** An annotation set drawn on the text: its annotations, and the names and colours of their labels. */
export interface DrawnAnnotations {
  readonly annotations: readonly IndexedAnnotation[];
  readonly labelNames: ReadonlyMap<string, string>;
  readonly labelOptions: AnnotationLabelOption[];
}

interface DocumentPartsProps {
  readonly document: Pick<SourceDocument, "ref" | "fullText" | "segments" | "metadata">;
  /** The spans drawn on the text; `null` draws none. */
  readonly annotations: DrawnAnnotations | null;
  /** Evidence on the text, anchored in code points of the whole document; `null` for none. */
  readonly evidence: EvidenceLayer | null;
  /** What sits beside each part, by segment path (`""` for a document without segments). */
  readonly asides?: ReadonlyMap<string, ReactNode>;
  /** Where selecting a part's text asks the agent about it (F96); `undefined` without a project. */
  readonly askInProject: string | undefined;
  /** What such a selection also sends: the annotation set on screen, so the agent reads the labels the reader saw (PC-6). */
  readonly askWith?: Omit<ChatReference, "quote"> | null;
}

const NO_ASIDES: ReadonlyMap<string, ReactNode> = new Map();
const NO_LABEL_NAMES: ReadonlyMap<string, string> = new Map();
const NO_PROBLEMS: ReadonlyMap<number, readonly string[]> = new Map();
const NO_MARKS: readonly EvidenceMark[] = [];

/**
 * Labelled evidence as the annotations' bars (Johannes, 4 Nov 06:51: "the annotation view is a must with the spans,
 * similar to the regest viewer"; 10:46: "I don'T see the spans"): each labelled piece in a part, under its exact
 * words, with its label at the span; one renderer for every span on the text.
 */
function labelledBars(text: string, marks: readonly EvidenceMark[]): SpannedAnnotationEntity<EditableAnnotationEntity>[] {
  return marks.flatMap((mark) => (mark.label === undefined || mark.end <= mark.start ? [] : [{
    id: `evidence:${mark.key}:${String(mark.start)}`, type: mark.label, value: text.slice(mark.start, mark.end), start: mark.start, end: mark.end,
    // The statement lists itself beside the text, once and without its quote (F94).
    unlisted: true,
  }]));
}

/**
 * A document's text, once, part by part (O-1): each part's name links to the part and names
 * its pages; its text carries the spans of the annotation set on screen and the evidence for
 * what sits beside it. Where any part has something beside it and the pane is wide, it sits
 * in a column next to its part; in a narrow pane it follows the part's text. The Documents
 * page's one view of the text: no second copy of it (F94).
 */
export default function DocumentParts({ document, annotations, evidence, asides = NO_ASIDES, askInProject, askWith = null }: DocumentPartsProps) {
  const { t } = useTranslation();
  const placement = useMemo(() => placeAnnotations(document, annotations?.annotations ?? []), [document, annotations]);
  const evidenceMarks = evidence?.marks;
  const evidenceByPart = useMemo(() => placeEvidence(document, evidenceMarks ?? []), [document, evidenceMarks]);
  const pages = useMemo(() => pageMapOf(document.metadata), [document.metadata]);
  // A labelled statement's bars take the colour of its kind ("#3 Litigation" as "Litigation"), the annotations' own.
  const labelOptions = useMemo(() => [
    ...(annotations?.labelOptions ?? []),
    ...[...new Set((evidenceMarks ?? []).flatMap((mark) => (mark.label === undefined ? [] : [mark.label])))]
      .map((label) => ({ ...getAnnotationLabelOption(label.replace(/^#\d+\s*/, "") || label), name: label })),
  ], [annotations, evidenceMarks]);
  if (document.fullText === "") {
    return <EmptyState className="px-4 py-6 sm:px-0">{t("sources.document.noFullText")}</EmptyState>;
  }
  const beside = asides.size > 0;
  return (
    <div className="app-divide-y">
      {placement.segments.map((part) => {
        const name = part.segmentPath === "" ? null : segmentDisplayName(part.segmentPath);
        const end = part.start + Array.from(part.text).length;
        return (
          <section
            key={part.segmentPath}
            id={part.segmentPath === "" ? undefined : `segment-${part.segmentPath}`}
            aria-label={name ?? undefined}
            className={classNames("py-3", beside && "pane-lg:grid pane-lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] pane-lg:gap-8")}
          >
            {/* Beside a long list, the part's text stays in view while it is read. */}
            <div className={classNames("min-w-0", beside && "pane-lg:sticky pane-lg:top-2 pane-lg:self-start")}>
              {name === null ? null : (
                <p className="flex flex-wrap items-baseline gap-x-3 text-xs">
                  <Link to={appPaths.sourceDocument(document.ref, part.segmentPath)} className="app-link font-semibold">{name}</Link>
                  <SegmentPages pages={pages} sourceUrl={document.metadata[SOURCE_URL_METADATA_KEY]} start={part.start} end={end} />
                </p>
              )}
              <AskAboutSegment projectId={askInProject} version={document.ref} segmentPath={part.segmentPath} name={name} enclosing={askWith}>
                <AnnotationTextEditor
                  text={part.text}
                  entities={[...part.annotations, ...labelledBars(part.text, evidenceByPart.get(part.segmentPath) ?? NO_MARKS)]}
                  evidence={evidence === null ? undefined : { ...evidence, marks: evidenceByPart.get(part.segmentPath) ?? NO_MARKS }}
                  framed={false}
                  labelNames={annotations?.labelNames ?? NO_LABEL_NAMES}
                  labelOptions={labelOptions}
                  selectLabel={t("threads.output.annotationLabel")}
                  addLabel={t("threads.output.annotationAdd")}
                  deleteLabel={t("threads.output.annotationDelete")}
                  annotationListLabel={name === null ? t("threads.output.annotationsInText") : t("threads.output.annotationsInSegment", { segment: name })}
                />
              </AskAboutSegment>
            </div>
            {asides.has(part.segmentPath) ? (
              <div className={classNames("mt-2 min-w-0 pane-lg:mt-0", name !== null && "pane-lg:pt-5")}>{asides.get(part.segmentPath)}</div>
            ) : null}
          </section>
        );
      })}
      <UnplacedAnnotations annotations={placement.unplaced} labelNames={annotations?.labelNames ?? NO_LABEL_NAMES} problems={NO_PROBLEMS} />
    </div>
  );
}
